import { Box3, Group, Object3D, Quaternion, Vector3 } from 'three';

import {
	APPLIANCE_SELECTION_GROUP_TYPE,
	createLabelSprite,
	indexToLetter,
	removeSelectionGroups
} from './selection-label.helper';
import { ToolResult } from '../../types/tool.types';
import { ToolContext } from '../tool-context.type';

const LABEL_FRONT_OFFSET = 18;

type ApplianceSelectionArgs = {
	applianceIds?: Array<string>;
	islandId?: string;
};

/**
 * Collect appliance Object3Ds from island groups, optionally filtered
 */
const collectAppliances = (
	islands: Array<Object3D>,
	args: ApplianceSelectionArgs
): Array<{ object: Object3D; islandId: string }> => {
	const { applianceIds, islandId } = args;
	const result: Array<{ object: Object3D; islandId: string }> = [];

	for (const islandGroup of islands) {
		const groupIslandId = islandGroup.userData?.id as string;
		if (!groupIslandId) continue;
		if (islandId && groupIslandId !== islandId) continue;

		islandGroup.traverse((child) => {
			if (child.userData?.type !== 'appliance') return;
			const childId = child.userData?.id as string;
			if (!childId) return;
			if (applianceIds && applianceIds.length > 0 && !applianceIds.includes(childId)) return;

			result.push({ object: child, islandId: groupIslandId });
		});
	}

	return result;
};

/**
 * Compute label position 18" in front of the appliance's front panel
 */
const getApplianceLabelPosition = (appliance: Object3D): Vector3 => {
	const worldPos = new Vector3();
	appliance.getWorldPosition(worldPos);

	// Get the appliance's front direction (local -Z in world space)
	const frontDir = new Vector3();
	appliance.getWorldDirection(frontDir);

	// Use userData.depth if available, otherwise estimate from bounding box
	const depth = (appliance.userData?.depth as number) || 0;

	// Position label in front of the appliance's front face
	const labelPos = worldPos.clone().add(
		frontDir.multiplyScalar((depth / 2) + LABEL_FRONT_OFFSET)
	);

	// Use bounding box for vertical centering
	const box = new Box3().setFromObject(appliance);
	const centerY = (box.min.y + box.max.y) / 2;
	labelPos.y = centerY;

	return labelPos;
};

export const getApplianceSelection = async (
	args: ApplianceSelectionArgs,
	context: ToolContext
): Promise<ToolResult> => {
	const { threeInstance, publish } = context;
	if (!threeInstance) {
		return { success: false, error: 'Three.js instance not initialized' };
	}

	const islands = threeInstance.getIslands();
	if (!islands || islands.length === 0) {
		return { success: false, error: 'No islands found in the scene' };
	}

	const objectHolder = islands[0].parent as Group;
	if (!objectHolder) {
		return { success: false, error: 'Could not access scene object holder' };
	}

	// Remove any existing appliance selection labels (idempotent)
	removeSelectionGroups(objectHolder, [APPLIANCE_SELECTION_GROUP_TYPE]);

	// Collect matching appliances
	const appliances = collectAppliances(islands, args || {});
	if (appliances.length === 0) {
		return { success: true, data: { mapping: {}, message: 'No matching appliances found' } };
	}

	// Create container group for labels
	const labelsGroup = new Group();
	labelsGroup.userData = { type: APPLIANCE_SELECTION_GROUP_TYPE };

	const mapping: Record<string, string> = {};

	appliances.forEach(({ object }, index) => {
		const letter = indexToLetter(index + 1);
		const applianceId = object.userData?.id as string;

		const sprite = createLabelSprite(letter);
		sprite.position.copy(getApplianceLabelPosition(object));
		sprite.userData = { id: applianceId, type: 'selection-label', label: letter };

		labelsGroup.add(sprite);
		mapping[letter] = applianceId;
	});

	objectHolder.add(labelsGroup);

	publish('appliances', 'selection-shown', {
		ref: 'appliance-selection',
		old: null,
		new: mapping
	});

	return { success: true, data: { mapping } };
};
